import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Activity, Clock, CheckCircle2, Users } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

const REFRESH_MS = 30_000;

/**
 * Painel "Saúde da Operação" — snapshot ao vivo dos atendimentos do dia.
 * Pode ser embutido no Dashboard, TodayOverview ou em qualquer lugar onde
 * o admin queira ver o pulso da operação.
 */
export function OperationsHealthCard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/today/operations-health");
      setData(data);
      setError(null);
    } catch (e) {
      setError("Falha ao carregar");
    }
  }
  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  if (!data && !error) return null;
  if (error) return null; // silencioso — não bloqueia o resto da página

  const blocks = [
    {
      key: "delayed",
      icon: AlertTriangle,
      label: t("Atrasados"),
      value: data.delayed_count,
      color: data.delayed_count > 0 ? "#dc2626" : "#94a3b8",
      bg: data.delayed_count > 0 ? "#fef2f2" : "#f8fafc",
      border: data.delayed_count > 0 ? "#fecaca" : "#e2e8f0",
    },
    {
      key: "in_progress",
      icon: Activity,
      label: t("Em andamento"),
      value: data.in_progress_count,
      color: "#0891b2",
      bg: "#ecfeff",
      border: "#a5f3fc",
    },
    {
      key: "soon",
      icon: Clock,
      label: t("Em 30min"),
      value: data.upcoming_30min_count,
      color: "#7c3aed",
      bg: "#faf5ff",
      border: "#e9d5ff",
    },
    {
      key: "done",
      icon: CheckCircle2,
      label: t("Concluídos"),
      value: `${data.finished_count}/${data.total_today}`,
      color: "#15803d",
      bg: "#f0fdf4",
      border: "#bbf7d0",
    },
    {
      key: "people",
      icon: Users,
      label: t("Operadores ativos"),
      value: data.active_collaborators_count,
      color: "#1e3a8a",
      bg: "#eff6ff",
      border: "#bfdbfe",
    },
  ];

  return (
    <div className="cm-card p-4 mb-5" data-testid="ops-health-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-blue-700">
            {t("Saúde da operação")}
          </div>
          <div className="text-sm text-slate-600">
            {t("Snapshot ao vivo · {pct}% concluído hoje", { pct: data.completion_pct })}
          </div>
        </div>
        <div className="text-[10px] text-slate-400 italic">
          {t("atualiza a cada 30s")}
        </div>
      </div>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}
      >
        {blocks.map((b) => (
          <div
            key={b.key}
            data-testid={`ops-block-${b.key}`}
            className="rounded-lg p-3 border"
            style={{ background: b.bg, borderColor: b.border }}
          >
            <div className="flex items-center gap-2">
              <b.icon size={14} style={{ color: b.color }} />
              <span
                className="text-[11px] font-bold uppercase tracking-wide"
                style={{ color: b.color }}
              >
                {b.label}
              </span>
            </div>
            <div className="text-2xl font-extrabold mt-1" style={{ color: b.color }}>
              {b.value}
            </div>
          </div>
        ))}
      </div>

      {(data.delayed?.length > 0 || data.upcoming?.length > 0) && (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          {data.delayed?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
              <div className="text-[11px] font-bold uppercase text-red-700 mb-1">
                {t("Atrasados agora ({n})", { n: data.delayed.length })}
              </div>
              <ul className="text-xs text-red-900 space-y-1">
                {data.delayed.slice(0, 3).map((j) => (
                  <li key={j.id}>
                    <Link
                      to={`/jobs/${j.id}`}
                      className="hover:underline font-semibold"
                    >
                      {new Date(j.scheduled_at).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Link>
                    <span className="text-red-700"> · {t("{n}min atrasado", { n: j.minutes_late })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.upcoming?.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5">
              <div className="text-[11px] font-bold uppercase text-purple-700 mb-1">
                {t("Próximos 30 min ({n})", { n: data.upcoming.length })}
              </div>
              <ul className="text-xs text-purple-900 space-y-1">
                {data.upcoming.slice(0, 3).map((j) => (
                  <li key={j.id}>
                    <Link
                      to={`/jobs/${j.id}`}
                      className="hover:underline font-semibold"
                    >
                      {new Date(j.scheduled_at).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Link>
                    <span className="text-purple-700"> · {t("em {n}min", { n: j.minutes_until })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
